/*
 * Tracks the AI chatbot and pushes Google Tag Manager dataLayer events:
 *     chat_opened, chat_started, chat_message_sent, chat_message_received,
 *     chat_starter_selected, chat_link_clicked, chat_restarted, chat_ended
 *
 * How it works:
 *   - Watches the server-rendered #chat-dialog (see _Chat.cshtml) for open/close (visibility).
 *   - Observes the Knockout-rendered .ws-chat__log for newly displayed user/assistant messages.
 *   - Reads the chat session id from the dialog's data-conversation-id attribute (set by chat.vm.js)
 *     and sends it as conversation_id on every event so events can be correlated.
 */
(function () {
    // Prevent double-initialization if the script is included more than once.
    if (window.__wsChatAnalyticsInitialized) {
        return;
    }

    window.__wsChatAnalyticsInitialized = true;
    window.dataLayer = window.dataLayer || [];

    const CHAT_SELECTOR = "#chat-dialog";
    const CHAT_LOG_SELECTOR = ".ws-chat__log";
    const CONVERSATION_ID_ATTRIBUTE = "data-conversation-id"; // data attribute that stores session id

    // Mutable tracking state for the current chat/conversation (reset per conversation).
    const state = {
        chatOpen: false,
        chatOpenTime: null,
        conversationId: null,
        conversationStarted: false,
        conversationStartTime: null,
        conversationNumber: 0,
        messageNumber: 0,
        userMessageCount: 0,
        assistantMessageCount: 0,
        lastUserMessageTime: null,
        pendingMessageSource: null,
        pendingStarterQuestionText: null,
        processedNodes: new WeakSet(),
        responseMessageNumbers: new WeakMap()
    };

    // ---- Small utilities ----

    // Milliseconds elapsed since a stored timestamp (0 when it was never set).
    function elapsedMilliseconds(startTime) {
        if (!startTime) {
            return 0;
        }

        return Date.now() - startTime;
    }

    // Collapse whitespace, trim, and lowercase — used for stable text comparison/matching.
    function normalizeText(text) {
        return String(text || "")
            .replace(/\s+/g, " ")
            .trim()
            .toLowerCase();
    }

    // ---- Conversation id (session correlation) ----

    // Read the chat session id exposed on #chat-dialog by chat.vm.js.
    function getConversationId(dialog) {
        if (!dialog) {
            return null;
        }

        // Set by the chat view model on #chat-dialog (falls back to a global if exposed).
        const attributeId = dialog.getAttribute(CONVERSATION_ID_ATTRIBUTE);

        if (attributeId) {
            return attributeId.trim() || null;
        }

        if (window.wsChatConversationId) {
            return String(window.wsChatConversationId).trim() || null;
        }

        return null;
    }

    // Cache the current session id into state so subsequent events can include it.
    function syncConversationId(dialog) {
        const conversationId = getConversationId(dialog);

        if (conversationId) {
            state.conversationId = conversationId;
        }
    }

    // ---- GTM event dispatch ----

    // Build a dataLayer payload (shared fields + conversation_id + params) and push it to GTM.
    function pushEvent(eventName, parameters) {
        const payload = {
            event: eventName,
            event_family: "chat",
            event_version: 1,
            chat_name: "ai_assistant"
        };

        if (state.conversationId) {
            payload.conversation_id = state.conversationId;
        }

        Object.keys(parameters || {}).forEach(function (key) {
            if (parameters[key] !== undefined && parameters[key] !== null) {
                payload[key] = parameters[key];
            }
        });

        window.dataLayer.push(payload);
    }

    // ---- Message de-duplication + numbering ----

    // processedNodes marks message elements already counted, so re-scans don't double-report.
    function hasProcessed(element) {
        return state.processedNodes.has(element);
    }

    function markProcessed(element) {
        state.processedNodes.add(element);
    }

    // Remember the message_number assigned to an assistant response (used by link-click events).
    function setResponseMessageNumber(element, messageNumber) {
        state.responseMessageNumbers.set(element, messageNumber);
    }

    function getResponseMessageNumber(element) {
        return state.responseMessageNumbers.get(element) || null;
    }

    // ---- DOM inspection helpers ----

    // The dialog counts as open only when it has ws-dialog--visible and is actually rendered.
    function isVisible(element) {
        if (!element) {
            return false;
        }

        const style = window.getComputedStyle(element);

        return (
            element.classList.contains("ws-dialog--visible") &&
            style.display !== "none" &&
            style.visibility !== "hidden" &&
            element.getClientRects().length > 0
        );
    }

    // Visible text of a chat bubble (scoped to the message text, excluding the response disclaimer).
    function getMessageText(element) {
        if (!element) {
            return "";
        }

        const textElement = element.querySelector(".ws-chat__log-message-group > .ws-chat__log-message-text");

        if (!textElement) {
            return "";
        }

        return String(textElement.textContent || "").replace(/\s+/g, " ").trim();
    }

    // Whether a paired user/system wrapper is the one Knockout actually made visible.
    function isRenderedMessage(element) {
        if (!element || element.hidden) {
            return false;
        }

        const style = window.getComputedStyle(element);

        return style.display !== "none" && style.visibility !== "hidden" && element.getClientRects().length > 0;
    }

    // Guard against malformed transient content that renders as raw serialized HTML.
    function appearsToBeSerializedHtml(text) {
        return /^\s*<(p|div|span|ul|ol|li|a|h[1-6])[\s>]/i.test(text);
    }

    // Classify an assistant response as fallback (couldn't answer confidently) or success.
    function classifySystemResponse(text) {
        const normalizedText = normalizeText(text);

        if (
            normalizedText.indexOf("i’m sorry, but i can’t answer that confidently") !== -1 ||
            normalizedText.indexOf("i'm sorry, but i can't answer that confidently") !== -1
        ) {
            return "fallback";
        }

        return "success";
    }

    // ---- Conversation lifecycle ----

    // Clear per-conversation counters after a conversation ends.
    function resetConversationMetrics() {
        state.conversationStarted = false;
        state.conversationStartTime = null;
        state.messageNumber = 0;
        state.userMessageCount = 0;
        state.assistantMessageCount = 0;
        state.lastUserMessageTime = null;
        state.pendingMessageSource = null;
        state.pendingStarterQuestionText = null;
    }

    // Fire chat_started once, on the first confirmed user message of a conversation.
    function startConversation(dialog, messageSource) {
        if (state.conversationStarted) {
            return;
        }

        syncConversationId(dialog);

        state.conversationStarted = true;
        state.conversationStartTime = Date.now();
        state.conversationNumber += 1;

        pushEvent("chat_started", {
            conversation_number: state.conversationNumber,
            message_source: messageSource,
            time_to_start_ms: elapsedMilliseconds(state.chatOpenTime)
        });
    }

    // Fire chat_ended (with duration + message counts when applicable), then reset metrics.
    function endChat(dialog, endReason) {
        syncConversationId(dialog);

        const conversationWasStarted = state.conversationStarted;

        let parameters = {
            conversation_number: state.conversationNumber,
            chat_duration_ms: elapsedMilliseconds(state.chatOpenTime),
            conversation_started: conversationWasStarted,
            conversation_end_reason: endReason
        };

        if (conversationWasStarted) {
            parameters.conversation_duration_ms = elapsedMilliseconds(state.conversationStartTime);
            parameters.user_message_count = state.userMessageCount;
            parameters.assistant_message_count = state.assistantMessageCount;
        }

        pushEvent("chat_ended", parameters);

        resetConversationMetrics();
    }

    // Translate dialog visibility transitions into chat_opened / chat_ended events.
    function handleChatVisibility(dialog) {
        const visible = isVisible(dialog);

        if (visible && !state.chatOpen) {
            state.chatOpen = true;
            state.chatOpenTime = Date.now();
            syncConversationId(dialog);
            pushEvent("chat_opened");
            return;
        }

        if (!visible && state.chatOpen) {
            endChat(dialog, "chat_closed");
            state.chatOpen = false;
            state.chatOpenTime = null;
            state.conversationId = null;
        }
    }

    // ---- Message log tracking ----

    // Decide whether the just-sent user message came from a starter question or was typed.
    function determineMessageSource(messageText) {
        if (
            state.pendingMessageSource === "starter_question" &&
            state.pendingStarterQuestionText &&
            normalizeText(messageText) === normalizeText(state.pendingStarterQuestionText)
        ) {
            return "starter_question";
        }

        return "typed";
    }

    // Inspect one direct child of .ws-chat__log and emit chat_message_sent/received as appropriate.
    function processMessageElement(dialog, log, element) {
        let text;
        let messageLength;
        let messageSource;
        let responseTime;

        if (!element || !element.parentElement || element.parentElement !== log || hasProcessed(element) || !isRenderedMessage(element)) {
            return;
        }

        text = getMessageText(element);

        messageLength = text.length;

        // Confirmed user message.
        if (element.classList.contains("ws-chat__log-user-message")) {
            // Exclude the static starter-question options and malformed temporary content.
            if (
                element.classList.contains("ws-chat__starter-question") ||
                element.querySelector(".ws-chat__starter-question") ||
                element.querySelector('[role="button"]') ||
                !text ||
                appearsToBeSerializedHtml(text)
            ) {
                return;
            }

            messageSource = determineMessageSource(text);
            markProcessed(element);

            // chat_started occurs only when the user message is confirmed in the dynamic log.
            startConversation(dialog, messageSource);

            state.messageNumber += 1;
            state.userMessageCount += 1;
            state.lastUserMessageTime = Date.now();

            pushEvent("chat_message_sent", {
                conversation_number: state.conversationNumber,
                message_number: state.messageNumber,
                message_source: messageSource,
                message_length: messageLength,
                conversation_elapsed_ms: elapsedMilliseconds(state.conversationStartTime)
            });

            state.pendingMessageSource = null;
            state.pendingStarterQuestionText = null;

            return;
        }

        // Confirmed assistant response.
        if (element.classList.contains("ws-chat__log-system-message")) {
            /*
             * A response is valid only after a confirmed
             * user message has started the conversation.
             *
             * assistantMessageCount < userMessageCount also
             * prevents duplicate response events for the
             * same user message.
             */
            if (
                !state.conversationStarted ||
                state.userMessageCount === 0 ||
                !state.lastUserMessageTime ||
                state.assistantMessageCount >= state.userMessageCount ||
                !text
            ) {
                return;
            }

            markProcessed(element);

            state.messageNumber += 1;
            state.assistantMessageCount += 1;

            setResponseMessageNumber(element, state.messageNumber);

            responseTime = Date.now() - state.lastUserMessageTime;

            syncConversationId(dialog);

            pushEvent("chat_message_received", {
                conversation_number: state.conversationNumber,
                message_number: state.messageNumber,
                message_length: messageLength,
                response_result: classifySystemResponse(text),
                response_time_ms: responseTime,
                conversation_elapsed_ms: elapsedMilliseconds(state.conversationStartTime)
            });
        }
    }

    // Observe the dynamic message log; scan for newly rendered messages after each mutation.
    function observeChatLog(dialog, log) {
        let observer;
        let scanScheduled = false;
        let existingMessages;

        // Ignore messages already present when the listener binds.
        existingMessages = log.children;

        Array.prototype.forEach.call(existingMessages, function (element) {
            markProcessed(element);
        });

        function scanVisibleMessages() {
            Array.prototype.forEach.call(log.children, function (element) {
                processMessageElement(dialog, log, element);
            });
        }

        function scheduleScan() {
            if (scanScheduled) {
                return;
            }

            scanScheduled = true;

            /*
             * Knockout renders paired user/system wrappers and then
             * applies visibility. Waiting until the next task lets the
             * final visible wrapper settle before it is classified.
             */
            window.setTimeout(function () {
                scanScheduled = false;
                scanVisibleMessages();
            }, 0);
        }

        observer = new MutationObserver(function () {
            scheduleScan();
        });

        observer.observe(log, {
            childList: true,
            subtree: true,
            characterData: true,
            attributes: true,
            attributeFilter: ["style", "class", "hidden", "aria-hidden"]
        });
    }

    // ---- Starter questions ----

    // Visible text of a starter-question button.
    function getStarterQuestionText(starterElement) {
        const textElement = starterElement.querySelector(".ws-chat__log-message-text");
        const starterText = textElement ? textElement.textContent : starterElement.getAttribute("aria-label") || starterElement.textContent || "";

        return String(starterText).replace(/\s+/g, " ").trim();
    }

    // Stable id for a starter question: a configured attribute, else a slug of its text.
    function createStarterQuestionId(starterElement) {
        let configuredId = starterElement.getAttribute("data-starter-question-id");

        if (!configuredId && starterElement.parentElement) {
            configuredId = starterElement.parentElement.getAttribute("data-starter-question-id");
        }

        if (configuredId) {
            return configuredId.trim();
        }

        let starterText = getStarterQuestionText(starterElement);

        /*
         * Example:
         *
         * What types of life insurance do you offer?
         *
         * becomes:
         *
         * what_types_of_life_insurance_do_you_offer
         */
        return starterText
            .toLowerCase()
            .replace(/&/g, " and ")
            .replace(/[’']/g, "")
            .replace(/[^a-z0-9]+/g, "_")
            .replace(/^_+|_+$/g, "");
    }

    // 1-based position of a starter question among all starter questions in the dialog.
    function getStarterQuestionPosition(dialog, starterElement) {
        const starters = dialog.querySelectorAll(".ws-chat__starter-question");

        for (let index = 0; index < starters.length; index += 1) {
            if (starters[index] === starterElement) {
                return index + 1;
            }
        }

        return null;
    }

    // ---- Link click tracking ----

    // True for westernsouthern.com and its subdomains (used to label links internal/external).
    function isWesternSouthernHostname(hostname) {
        hostname = String(hostname || "").toLowerCase();
        return hostname === "westernsouthern.com" || hostname.slice(-".westernsouthern.com".length) === ".westernsouthern.com";
    }

    // Relative path for same-host links; the full URL for anything off-host.
    function getLinkUrl(anchor) {
        try {
            const destination = new URL(anchor.href, window.location.href);

            /*
             * Use a relative path only when the destination
             * is on the exact current hostname.
             *
             * Links to another W&S subdomain retain the full
             * URL so the destination hostname is preserved.
             */
            if (destination.hostname === window.location.hostname) {
                return destination.pathname + destination.search + destination.hash;
            }

            return destination.href;
        } catch (error) {
            return anchor.getAttribute("href") || "";
        }
    }

    // Label a link internal / external / other (an explicit data-*-link-type wins).
    function classifyLink(anchor) {
        let configuredType = anchor.getAttribute("data-chat-link-type") || anchor.getAttribute("data-link-type");

        if (configuredType) {
            return configuredType.trim();
        }

        try {
            const destination = new URL(anchor.href, window.location.href);
            if (isWesternSouthernHostname(destination.hostname)) {
                return "internal";
            }
            return "external";
        } catch (error) {
            return "other";
        }
    }

    // 1-based position of a link within its assistant response bubble.
    function getLinkPosition(responseElement, selectedLink) {
        const links = responseElement.querySelectorAll("a[href]");

        for (let index = 0; index < links.length; index += 1) {
            if (links[index] === selectedLink) {
                return index + 1;
            }
        }

        return null;
    }

    // Emit chat_link_clicked when a link inside an assistant response is clicked. Returns true if handled.
    function trackChatLinkClick(dialog, clickedElement) {
        const anchor = clickedElement.closest(".ws-chat__log-system-message a[href]");

        if (!anchor) {
            return false;
        }

        const responseElement = anchor.closest(".ws-chat__log-system-message");
        const log = dialog.querySelector(CHAT_LOG_SELECTOR);

        if (!responseElement || !log || responseElement.parentElement !== log) {
            return false;
        }

        let linkText = String(anchor.textContent || "")
            .replace(/\s+/g, " ")
            .trim();

        syncConversationId(dialog);

        pushEvent("chat_link_clicked", {
            conversation_number: state.conversationNumber,
            message_number: getResponseMessageNumber(responseElement),
            link_text: linkText,
            link_url: getLinkUrl(anchor),
            link_type: classifyLink(anchor),
            link_position: getLinkPosition(responseElement, anchor)
        });

        return true;
    }

    // ---- Wiring / initialization ----

    // Delegated listeners on the dialog for starter questions, links, new-chat, and sends.
    function bindChatInteractions(dialog) {
        // Capture the starter selection before the chat application handles the click and renders the submitted message.
        dialog.addEventListener(
            "click",
            function (event) {
                const starter = event.target.closest(".ws-chat__starter-question");

                if (!starter) {
                    return;
                }

                syncConversationId(dialog);

                state.pendingMessageSource = "starter_question";
                state.pendingStarterQuestionText = getStarterQuestionText(starter);

                pushEvent("chat_starter_selected", {
                    starter_question_id: createStarterQuestionId(starter),
                    starter_question_position: getStarterQuestionPosition(dialog, starter)
                });
            },
            true
        );

        dialog.addEventListener("click", function (event) {
            const target = event.target;

            if (trackChatLinkClick(dialog, target)) {
                return;
            }

            // Starter questions are handled by the capture listener.
            if (target.closest(".ws-chat__starter-question")) {
                return;
            }

            if (target.closest(".ws-chat__new-chat-button")) {
                syncConversationId(dialog);

                const conversationWasStarted = state.conversationStarted;
                const currentConversationNumber = state.conversationNumber;

                endChat(dialog, "new_chat");

                pushEvent("chat_restarted", {
                    conversation_number: currentConversationNumber,
                    conversation_started: conversationWasStarted
                });

                state.chatOpenTime = Date.now();
                state.conversationId = null;
                return;
            }

            const sendButton = target.closest(".ws-chat__send-message-button");

            if (sendButton && !sendButton.disabled) {
                markTypedIfInputPresent(dialog);
            }
        });

        dialog.addEventListener("keydown", function (event) {
            const input = event.target.closest(".ws-chat__send-message-input");

            if (input && event.key === "Enter" && !event.shiftKey && String(input.value || "").trim() !== "") {
                state.pendingMessageSource = "typed";
                state.pendingStarterQuestionText = null;
            }
        });
    }

    // Flag the next submission as "typed" when the message input currently has content.
    function markTypedIfInputPresent(dialog) {
        const input = dialog.querySelector(".ws-chat__send-message-input");

        if (input && String(input.value || "").trim() !== "") {
            state.pendingMessageSource = "typed";
            state.pendingStarterQuestionText = null;
        }
    }

    // Bind analytics to a dialog exactly once: message-log observer + visibility/id observer.
    function initializeChat(dialog) {
        if (!dialog || dialog.getAttribute("data-chat-analytics-bound") === "true") {
            return;
        }

        dialog.setAttribute("data-chat-analytics-bound", "true");

        syncConversationId(dialog);

        const log = dialog.querySelector(CHAT_LOG_SELECTOR);

        handleChatVisibility(dialog);
        bindChatInteractions(dialog);

        if (log) {
            observeChatLog(dialog, log);
        }

        const dialogObserver = new MutationObserver(function (mutations) {
            mutations.forEach(function (mutation) {
                if (mutation.type === "attributes" && mutation.attributeName === CONVERSATION_ID_ATTRIBUTE) {
                    syncConversationId(dialog);
                }
            });

            handleChatVisibility(dialog);
        });

        dialogObserver.observe(dialog, {
            attributes: true,
            attributeFilter: ["class", "style", "hidden", "aria-hidden", CONVERSATION_ID_ATTRIBUTE]
        });
    }

    // Initialize as soon as #chat-dialog exists (now, or when it is inserted into the DOM).
    function waitForChat() {
        const existingDialog = document.querySelector(CHAT_SELECTOR);

        if (existingDialog) {
            initializeChat(existingDialog);
            return;
        }

        // #chat-dialog is server-rendered; watch only until it appears, then stop.
        const pageObserver = new MutationObserver(function () {
            const dialog = document.querySelector(CHAT_SELECTOR);

            if (dialog) {
                pageObserver.disconnect();
                initializeChat(dialog);
            }
        });

        pageObserver.observe(document.documentElement, {
            childList: true,
            subtree: true
        });
    }

    waitForChat();
})();
